import React, { useEffect, useRef, useState } from 'react';
import {Button, Image, message, Upload} from 'antd';
import {
  DownloadOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { formatter, request, ResponseStatus } from '@/utils/request';
import {
  fileDownload, fileDownloadBase64, fileDownloadBlob,
  fileObjectURL,
  formatFileDownload,
  isAnImage
} from '@/utils/download';
import type { UploadProps } from 'antd/es/upload';
import type { UploadFile } from 'antd/lib/upload/interface';
import { wdiCore } from './wdiCore';
import { Enum } from '@/enums/system';
import {WdiFileItem, WdiFileItemRef} from "@/components/WdiControls/WdiFileItem";
import {WdiLoading} from "@/components/WdiControls/index";

export const parseUrl = async (file: any) => {
  if (file.url) {
    let hostFileCust = window.location.host + '/image/';
    if (HOST_FILE_CUSTOM.startsWith("http")) {
      hostFileCust = HOST_FILE_CUSTOM;
    }
    if (file.url?.indexOf(Enum.AttachmentCustomPrefix.FILE_CUSTOM_PREFIX) > -1) {
      return hostFileCust + file.url?.substring(Enum.AttachmentCustomPrefix.FILE_CUSTOM_PREFIX.length);
    } else if (file.url.indexOf(Enum.AttachmentCustomPrefix.OBS_CUSTOM_PREFIX) > -1) {
      return hostFileCust + file.url.substring(Enum.AttachmentCustomPrefix.OBS_CUSTOM_PREFIX.length);
    } else {
      return await fileObjectURL(formatFileDownload(file));
    }
  } else {
    return '';
  }
}

export type WdiUploadProps = {
  leftTopInfo?: string;
  addToAttachment?: boolean; //附件存储到数据库的附件表格
  preview?: boolean; //是否预览图片
  generateType?: number;//生成文件的类型，默认为1
} & UploadProps;

export const WdiFileUpload: React.FC<WdiUploadProps> = (props) => {
  const [previewUrl, setPreviewUrl] = useState<string>();
  const groupIds:Set<string> = new Set<string>();
  const { leftTopInfo, addToAttachment, preview, generateType, ...newProps } = Object.assign({}, props);

  const showPreview = (file: UploadFile) => {
    const index = file.name?.lastIndexOf('.');
    const fnExtension = file.name?.substring(Number(index) + 1);
    if (isAnImage(fnExtension)) {
      parseUrl(file).then(url => {
        setPreviewUrl(url)
      })
    }
  }

  if (preview && wdiCore.Object.IsNullOrUndefined(previewUrl) && props.fileList && props.fileList.length > 0) {
    showPreview(props.fileList[0]);
  }

  const beforeUpload = (file:any, fileList:any[]) => {
    if(!fileList?.length){
      return Upload.LIST_IGNORE;
    }
    const uid = file.uid;
    if(!uid){
      message.error("文件属性异常，请刷新后重试!");
      return Upload.LIST_IGNORE;
    }
    const lastFile= fileList[fileList.length-1]
    const groupId = uid.substring(0,uid.lastIndexOf('-'))
    const allFileLength = (props.fileList?.length ?? 0) + fileList.length;
    if (fileList.length > MAX_SELECT_UPLOAD_COUNT && !groupIds.has(groupId)) {
      message.error(`最多只能同时选择 ${MAX_SELECT_UPLOAD_COUNT} 个文件！`);
      groupIds.add(groupId)
    }else if(allFileLength > MAX_UPLOAD_COUNT && !groupIds.has(groupId)){
      message.error(`最多只能上传${MAX_UPLOAD_COUNT}个文件,已上传${props.fileList?.length ?? 0}个文件,本次上传的${fileList.length}个文件将被取消,请重新上传`);
      groupIds.add(groupId)
    }
    if(groupIds.has(groupId)){
      if(lastFile.uid == file.uid){
        groupIds.delete(groupId);
      }
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const customRequest = (componentsData: any) => {
    const { file } = componentsData;
    const formData = new FormData();
    let generateTypeValue : number = generateType || Enum.AttachmentGenerateType.DEFAULT;
    if (addToAttachment === false) {
      generateTypeValue = Enum.AttachmentGenerateType.TEMP;
    }
    formData.append('fileInfo', file);
    formData.append('generateType', String(generateTypeValue)); //用户上传附件

    request<API.ResponseResult<API.FileItem>>(
      '/fileapi/file/upload',
      formatter({ method: 'POST', body: formData }),
    ).then(res => {
      if (res.code == ResponseStatus.SUCCESS) {
        file.url = res.data?.path;
        if (preview) {
          showPreview(file);
        }
        componentsData.onSuccess();
      } else {
        componentsData.onError();
      }
    }).catch((error) => {
      componentsData.onError();
    });
  };

  const antdProps = Object.assign({}, newProps);
  const disabled = !!antdProps.disabled;
  if(!disabled){
    antdProps.beforeUpload = beforeUpload
  }
  if (!antdProps.action && !antdProps.customRequest) {
    antdProps.customRequest = customRequest;
  }
  if (!antdProps.maxCount || antdProps.maxCount > 1) {
    antdProps.multiple = true;
  }
  if (!antdProps.itemRender) {
    antdProps.itemRender = (originNode: any, file: UploadFile, fileList: object[], actions: any) => {
      return (
        <WdiFileItem
          file={file}
          disabled={disabled}
          onDel={() => {
            actions.remove?.();
            setPreviewUrl('');
          }}
        />
      );
    }
  }

  if (!antdProps.onDownload) {
    antdProps.onDownload = FileUploadUtility.download;
  }

  // if (!antdProps.maxCount) {
  //   if (wdiCore.Object.IsNullOrUndefined(antdProps.multiple)) {
  //     antdProps.multiple = true;
  //   }
  // }

  if (disabled) {
    return (
      <Upload {...antdProps}>
      </Upload>
    )
  }
  return (
    <div className='upload-container' style={{ display: 'inline' }}>
      <Upload {...antdProps}>
        <Button style={{ backgroundColor: '#efefef' }} icon={<UploadOutlined />}>浏览</Button>
        {leftTopInfo && <span style={{ paddingLeft: "24px", cursor: 'pointer' }}>{leftTopInfo}</span>}
      </Upload>
      {preview && previewUrl && <Image width={200} src={previewUrl} />}
    </div>
  )
};

export const FileUploadUtility = {
  download: (file: UploadFile) => {
    return fileDownload(file);
  },
  downloadBlob: (file: UploadFile) => {
    return fileDownloadBlob(file);
  },
  downloadBase64: (file: UploadFile) => {
    return fileDownloadBase64(file);
  },
  parse: function (names: string | null | undefined, urls: string | null | undefined) {
    let fileNames: string[] = [];
    if (names) {
      fileNames = names.split(";#");
    }
    let filePaths: string[] = [];
    if (urls) {
      filePaths = urls.split(";#");
    }
    var values = [];
    if (fileNames.length === filePaths.length) {
      for (var index = 0; index < fileNames.length; index++) {
        values.push({
          uid: String(index + 1),
          name: fileNames[index],
          url: filePaths[index]
        });
      }
    }

    return values;
  },
  serialize: (fileList: UploadFile[] | undefined | null) => {
    var names = "";
    var urls = "";
    var spliter = ";#";
    if (fileList) {
      fileList.forEach((item, itemIndex) => {
        if (itemIndex !== 0) {
          names += spliter;
          urls += spliter;
        }
        names += item.name;
        urls += item.url;
      });
    }
    return { names: names, urls: urls };
  },
  getFileListByStatus: (fileList: UploadFile[] | undefined | null, status: string) => {
    const newFileList: UploadFile[] = [];
    if (fileList) {
      fileList.forEach((item) => {
        if (item.status == status) {
          newFileList.push(item);
        }
      })
    }
    return newFileList;
  },
  isUploading: (fileList: UploadFile[] | undefined | null) => {
    return FileUploadUtility.getFileListByStatus(fileList, 'uploading').length > 0;
  },
  isError: (fileList: UploadFile[] | undefined | null) => {
    return FileUploadUtility.getFileListByStatus(fileList, 'error').length > 0;
  }
};


type WdiFileListProps = {
  fileList?: UploadFile[];
  height?: number | string;
  allowDownloadAll?: boolean;
  onFileClick?: (item: UploadFile) => (e: any) => void;
};

export const WdiFileList: React.FC<WdiFileListProps> = (props) => {
  const [imageState, setImageState] = useState<{ visible: boolean, url?: string }>();
  const [downloadAllLoading,setDownLoadAllLoading] = useState<boolean>(false)
  const itemRefs = useRef<(WdiFileItemRef | null)[]>([]);

  const downloadAll = async () => {
    if(!itemRefs?.current.length){
      return;
    }
    setDownLoadAllLoading(true)
    Promise.all(itemRefs.current.filter(item=>item).map(item=>{
      return item?.itemDownload();
    })).then(res=>{
      setDownLoadAllLoading(false);
      message.success("下载成功")
    })
  };

  return (
    <>
      {props.fileList && props.fileList.length > 1 && (wdiCore.Object.IsNullOrUndefined(props.allowDownloadAll) || props.allowDownloadAll) &&
        <Button type="primary" shape="round" icon={<DownloadOutlined />} size='small' style={{ position: 'absolute', right: 0 }}
          onClick={() => { downloadAll(); }}
        >全部下载</Button>
      }
      <WdiLoading visible={downloadAllLoading}/>
      <div className="ant-upload-list ant-upload-list-text" style={{ maxHeight: props.height || 100, overflowY: 'auto', width: '80%' }}>
        {
          (props.fileList ? props.fileList : []).map((item: UploadFile, index: number) => {
            return (
              <WdiFileItem ref={el => { itemRefs.current[index] = el; }} file={item}/>
            )
          })
        }
      </div>
      {
        imageState?.visible && <Image
          width={400}
          style={{ display: 'none' }}
          src={imageState.url}
          preview={{
            visible: imageState.visible,
            src: imageState.url,
            onVisibleChange: (value: any) => {
              setImageState({ visible: value });
            },
          }}
        />
      }
    </>
  )
}

type WdiImageProps = {
  name?: string;
  url: string;
  width: number;
  height: number;
};

export const WdiAttachmentImage: React.FC<WdiImageProps> = (props: WdiImageProps) => {

  const [imgUrl, setImgUrl] = useState<string>();
  const [canPreview, setCanPreview] = useState(false);
  const previewStr = `?x-image-process=image/resize,m_lfit,h_${props.width},w_${props.height}`;

  useEffect(() => {
    if (props.url) {
      if (props.url.indexOf(Enum.AttachmentCustomPrefix.FILE_CUSTOM_PREFIX) > -1) {
        setImgUrl(HOST_FILE_CUSTOM + props.url.substring(Enum.AttachmentCustomPrefix.FILE_CUSTOM_PREFIX.length));
        setCanPreview(true);
      } else if (props.url.indexOf(Enum.AttachmentCustomPrefix.OBS_CUSTOM_PREFIX) > -1) {
        setImgUrl(HOST_FILE_CUSTOM + props.url.substring(Enum.AttachmentCustomPrefix.OBS_CUSTOM_PREFIX.length));
        setCanPreview(true);
      }
      else {
        fileObjectURL(formatFileDownload({ name: props.name, url: props.url })).then(response => {
          setImgUrl(response);
        });
      }
    } else {
      setImgUrl("");
    }
  }, [props.url]);

  return (
    <>
      {
        imgUrl &&
        <Image src={canPreview ? imgUrl + previewStr : imgUrl}
          width={props.width}
          height={props.height}
          preview={{ src: imgUrl }}
        />
      }
    </>
  )
}
